import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  PLATFORM_ID,
  Inject,
  HostListener,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  LucideAngularModule,
  ArrowLeft,
  Info,
  Loader2,
  Search,
  AlertCircle,
  CheckCircle,
} from 'lucide-angular';

// Importar QuaggaJS
import * as Quagga from 'quagga';
import { Auto } from '../../../types/vehicle.types';
import { VehicleService } from '../../../services/vehicle.service';
import { NotificationService } from '../../../services/notification.service';

@Component({
  selector: 'allas-vin-scanner-modal',
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './vin-scanner-modal.html',
  styleUrls: ['./vin-scanner-modal.scss'],
})
export class VinScannerModal implements OnInit, OnDestroy {
  @Input() isModal: boolean = false;
  @Output() closed = new EventEmitter<void>();
  @Output() vinFound = new EventEmitter<Auto>();

  @ViewChild('videoElement') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('scannerContainer') scannerContainer!: ElementRef<HTMLDivElement>;

  readonly arrowLeftIcon = ArrowLeft;
  readonly infoIcon = Info;
  readonly loader2Icon = Loader2;
  readonly searchIcon = Search;
  readonly alertCircleIcon = AlertCircle;
  readonly checkCircleIcon = CheckCircle;

  vin: string = '';
  isScanning: boolean = false;
  isSearching: boolean = false;
  showVinGuide: boolean = false;
  showManualInput: boolean = false;
  errorMessage: string = '';
  successMessage: string = '';
  scanAttempts: number = 0;
  maxScanAttempts: number = 3;
  isQuaggaInitialized: boolean = false;
  scannerStatus: 'initializing' | 'ready' | 'error' | 'scanning' =
    'initializing';

  private stream: MediaStream | null = null;
  private quaggaInstance: any = null;
  private isProcessingScan: boolean = false;

  constructor(
    private vehicleService: VehicleService,
    private notificationService: NotificationService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.initializeModal();
  }

  ngOnDestroy(): void {
    this.stopScanner();
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: Event): void {
    const keyboardEvent = event as KeyboardEvent;
    // Solo procesar Enter si el modal está abierto y estamos en modo manual
    if (this.showManualInput && this.vin.length === 17) {
      keyboardEvent.preventDefault();
      this.handleManualSearch();
    }
  }

  private initializeModal(): void {
    // Reset state when modal opens
    this.vin = '';
    this.isScanning = false;
    this.isSearching = false;
    this.showVinGuide = false;
    this.showManualInput = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.scanAttempts = 0;
    this.isQuaggaInitialized = false;
    this.scannerStatus = 'initializing';
    this.isProcessingScan = false;

    if (isPlatformBrowser(this.platformId)) {
      this.startScanner();
    }
  }

  private async startScanner(): Promise<void> {
    try {
      this.scannerStatus = 'initializing';
      this.errorMessage = '';

      // Verificar si estamos en el navegador
      if (!isPlatformBrowser(this.platformId)) {
        this.showManualInput = true;
        return;
      }

      // Esperar un poco para que el DOM se renderice completamente
      await new Promise((resolve) => setTimeout(resolve, 200));

      // Verificar si Quagga está disponible
      if (typeof Quagga !== 'undefined') {
        await this.initializeQuaggaScanner();
      } else {
        console.error('QuaggaJS no está disponible');
        this.scannerStatus = 'error';
        this.errorMessage = 'Error: QuaggaJS no está disponible';
        this.showManualInput = true;
      }
    } catch (err) {
      console.error('Error starting scanner:', err);
      this.scannerStatus = 'error';
      this.errorMessage =
        'Error al inicializar el escáner. Usando entrada manual.';
      this.showManualInput = true;
    }
  }

  private async initializeQuaggaScanner(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        // Detener Quagga si ya está inicializado
        if (this.isQuaggaInitialized) {
          Quagga.stop();
        }

        // Esperar a que el elemento del DOM esté disponible
        let targetElement: HTMLElement | null =
          this.scannerContainer?.nativeElement;

        if (!targetElement) {
          targetElement = document.querySelector('#scanner-container');
        }

        if (!targetElement) {
          console.error(
            'Elemento del scanner no encontrado, intentando crear uno'
          );
          // Crear un elemento temporal si no existe
          const tempElement = document.createElement('div');
          tempElement.id = 'scanner-container';
          tempElement.style.width = '100%';
          tempElement.style.height = '100%';
          document.body.appendChild(tempElement);
          targetElement = tempElement;
        }

        if (!targetElement) {
          console.error('No se pudo crear el elemento del scanner');
          this.scannerStatus = 'error';
          this.errorMessage = 'Error: No se pudo inicializar el scanner';
          reject(new Error('No se pudo crear el elemento del scanner'));
          return;
        }

        const config = {
          inputStream: {
            name: 'Live',
            type: 'LiveStream',
            target: targetElement,
            constraints: {
              width: { min: 640, max: 1280 },
              height: { min: 480, max: 720 },
              facingMode: 'environment',
            },
          },
          locator: {
            patchSize: 'medium',
            halfSample: true,
          },
          numOfWorkers: 2,
          frequency: 10,
          decoder: {
            readers: [
              'code_128_reader',
              'ean_reader',
              'code_39_reader',
              'code_39_vin_reader',
              'codabar_reader',
              'upc_reader',
            ],
          },
          locate: true,
        };

        Quagga.init(config, (err: any) => {
          if (err) {
            console.error('Quagga initialization failed:', err);
            this.scannerStatus = 'error';
            this.errorMessage = `Error al inicializar Quagga: ${
              err.message || 'Error desconocido'
            }`;
            reject(err);
            return;
          }

          this.isQuaggaInitialized = true;
          this.quaggaInstance = Quagga;
          this.scannerStatus = 'ready';

          // Configurar el evento de detección
          Quagga.onDetected(this.onBarcodeDetected.bind(this));

          // Iniciar el escáner
          Quagga.start();
          resolve();
        });
      } catch (error) {
        console.error('Error en initializeQuaggaScanner:', error);
        this.scannerStatus = 'error';
        this.errorMessage = 'Error al configurar el escáner';
        reject(error);
      }
    });
  }

  private onBarcodeDetected(result: any): void {
    if (this.isProcessingScan || this.isSearching || this.isScanning) {
      return;
    }

    const code = result.codeResult?.code;

    if (!code) {
      return;
    }

    // Verificar si el código tiene 17 caracteres (VIN típico)
    if (code.length === 17) {
      this.isProcessingScan = true;
      this.scannerStatus = 'scanning';
      this.vin = code.toUpperCase();

      // Detener Quagga temporalmente
      if (this.isQuaggaInitialized) {
        Quagga.stop();
      }

      this.handleScanSuccess();
    } else {
      this.scanAttempts++;
      const message = `Código inválido (${code.length} caracteres). Intento ${this.scanAttempts}/${this.maxScanAttempts}`;
      this.errorMessage = message;

      if (this.scanAttempts >= this.maxScanAttempts) {
        this.errorMessage =
          'Máximo de intentos alcanzado. Usando entrada manual.';
        setTimeout(() => {
          this.showManualInput = true;
        }, 2000);
      }
    }
  }

  private stopScanner(): void {
    // Detener Quagga si está inicializado
    if (this.isQuaggaInitialized && this.quaggaInstance) {
      try {
        this.quaggaInstance.stop();
        this.isQuaggaInitialized = false;
        this.quaggaInstance = null;
      } catch (error) {
        console.error('Error stopping Quagga:', error);
      }
    }

    // Detener stream de cámara si existe
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    this.scannerStatus = 'initializing';
    this.isProcessingScan = false;
  }

  async handleManualSearch(): Promise<void> {
    if (this.vin.length !== 17) {
      this.errorMessage = 'El VIN debe tener exactamente 17 caracteres';
      this.notificationService.showError(
        'El VIN debe tener exactamente 17 caracteres'
      );
      return;
    }

    this.isSearching = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const vehicleData = await this.vehicleService.decodeVin(this.vin);
      if (vehicleData) {
        this.successMessage = `Vehículo encontrado: ${vehicleData.anio} ${vehicleData.marca} ${vehicleData.modelo}`;
        setTimeout(() => {
          this.vinFound.emit(vehicleData);
        }, 1000);
      } else {
        this.errorMessage =
          'No se encontró información para este VIN. Verifica que el número sea correcto.';
        this.notificationService.showError(
          'No se encontró información para este VIN. Verifica que el número sea correcto.'
        );
      }
    } catch (error) {
      console.error('Error searching VIN:', error);
      this.errorMessage =
        'Error al buscar el VIN. Verifica tu conexión a internet e intenta nuevamente.';
      this.notificationService.showError(
        'Error al buscar el VIN. Verifica tu conexión a internet e intenta nuevamente.'
      );
    } finally {
      this.isSearching = false;
    }
  }

  // Método de prueba para verificar la funcionalidad
  async testVinDecode(): Promise<void> {
    this.vin = '2HGES16553H585409';
    await this.handleManualSearch();
  }

  async handleScanSuccess(): Promise<void> {
    this.isScanning = true;
    this.errorMessage = '';
    this.successMessage = '';

    try {
      const vehicleData = await this.vehicleService.decodeVin(this.vin);
      if (vehicleData) {
        this.successMessage = `Vehículo encontrado: ${vehicleData.anio} ${vehicleData.marca} ${vehicleData.modelo}`;
        setTimeout(() => {
          this.vinFound.emit(vehicleData);
        }, 1000);
      } else {
        this.errorMessage =
          'No se pudo decodificar el VIN escaneado. Intenta ingresarlo manualmente.';
      }
    } catch (error) {
      console.error('Error scanning VIN:', error);
      this.errorMessage =
        'Error al procesar el VIN escaneado. Intenta ingresarlo manualmente.';
    } finally {
      this.isScanning = false;
      this.isProcessingScan = false;
    }
  }

  onClose(): void {
    this.stopScanner();
    this.closed.emit();
  }

  onVinInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.vin = target.value.toUpperCase();
    // Clear messages when user starts typing
    this.errorMessage = '';
    this.successMessage = '';
  }

  toggleVinGuide(): void {
    this.showVinGuide = !this.showVinGuide;
  }

  backToScanner(): void {
    this.showManualInput = false;
    this.errorMessage = '';
    this.successMessage = '';
    this.scanAttempts = 0;
    this.isProcessingScan = false;
    this.scannerStatus = 'initializing';
    if (isPlatformBrowser(this.platformId)) {
      this.startScanner();
    }
  }

  // Método para reiniciar el scanner
  restartScanner(): void {
    this.stopScanner();
    setTimeout(() => {
      this.startScanner();
    }, 1000);
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  // Método para simular escaneo exitoso (para pruebas)
  simulateSuccessfulScan(): void {
    this.vin = '2HGES16553H585409';
    this.handleScanSuccess();
  }
}
